// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: LGPL-2.1-only

import type { QdsDialog, Size } from "@quartzds/core";
import type { ComponentInterface } from "@stencil/core";
import {
  Component,
  Fragment,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import type { JsxElement } from "typescript";

import {
  getResponsiveImageSource,
  getYoukuVideoId,
  getYoutubeVideoId,
} from "../utils";

type Origin = "native" | "youku" | "youtube";
// type ButtonAction = "modal-button" | "player-button";
type ButtonSize = Size | "hero";
type SubtitlesKind = "captions" | "chapters" | "metadata" | "subtitles";
type VideoType = "video/mp4" | "video/webm";

@Component({
  tag: "qds-web-media",
  shadow: true,
  styleUrl: "media.css",
})
export class Media implements ComponentInterface {
  /**
   * The URL of the video to embed.
   * If not specified, the image will be shown.
   * @remarks It can be a path for native video, a youtube link or a youku link.
   *
   * @public
   * @readonly
   * @type {string}
   * */
  @Prop() public srcVideo: string;

  /**
   * If specified, the video automatically begins to play back as soon as it can without stopping to finish loading the data.
   *
   * @public
   * @readonly
   * @type {boolean}
   * @default false
   * */
  @Prop() public autoPlay?: boolean = false;

  /**
   * Descriptive name for the video element
   *
   * @public
   * @readonly
   * @type {string}
   * */
  @Prop() public labelName: string;

  /**
   * Can be used to instruct the browser to defer loading of images/iframes that are off-screen until the user scrolls near them.
   * Lazy: Used to defer fetching a resource until some conditions are met.
   * Eager: Used to fetch a resource immediately.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/Performance/Lazy_loading#images_and_iframes
   * @public
   * @readonly
   * @type {HTMLImageElement["loading"]}
   * @default "lazy"
   * */
  @Prop() public loading?: HTMLImageElement["loading"] = "lazy";

  /**
   * If specified, the browser will automatically seek back to the start upon reaching the end of the video.
   *
   * @public
   * @readonly
   * @type {boolean}
   * @default false
   * */
  @Prop() public loop?: boolean = false;

  /**
   * Indicates the default audio mute setting contained in the video.
   *
   * @public
   * @readonly
   * @type {boolean}
   * @default false
   * */
  @Prop() public muted?: boolean = false;

  /**
   * The video will open in a modal
   * @remarks Youtube and Youku videos are always played in a modal
   *
   * @public
   * @readonly
   * @type {boolean}
   * @default false
   * */
  @Prop() public openModal?: boolean = false;

  /**
   * A URL for an image to be shown.
   * In videos it's shown while the video is downloading. If this attribute isn't specified, nothing is displayed until the first frame is available, then the first frame is shown as the poster frame.
   * @remarks This image will be shown when the video opens in a modal. To specify the source path or url based on the responsive image srcset attribute add one or more strings separated by commas, indicating possible image sources for the user agent to use.
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#srcset
   *
   * @public
   * @readonly
   * @type {string}
   * */
  @Prop() public imageSrc?: string;

  /**
   * Allows to specify the layout width of the image for each of a list of media conditions. The value should be a string containing a comma-separated list of source size descriptors followed by an optional fallback size.
   * @remark Combined with a srcSet content in the imageSrc property, this provides the ability to automatically select among different images—even images of different orientations or aspect ratios—as the document state changes to match different media conditions.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/sizes
   *
   * @public
   * @readonly
   * @type {string}
   *
   */
  @Prop() public readonly imageSizes?: string;

  /**
   * Alt text for the image to display.
   *
   * @public
   * @readonly
   * @type {string}
   */
  @Prop() public readonly imageAlt?: string;

  /**
   * Specifies the MIME media type of the image or other media type
   *
   * @public
   * @readonly
   * @type {VideoType}
   * @default video/mp4
   * */
  @Prop() public type?: VideoType = "video/mp4";

  /**
   * The button's size.
   *
   * @public
   * @readonly
   * @type {ButtonSize}
   * */
  @Prop() public buttonSize?: ButtonSize = "standard";

  /**
   * Address of the track (.vtt file). Must be a valid URL. This attribute must be specified and its URL value must have the same origin as the document
   *
   * @public
   * @readonly
   * @type {VideoType}
   * */
  @Prop() public srcSubtitles?: string;

  /**
   * How the text track is meant to be used. If omitted the default kind is subtitles. If the attribute contains an invalid value, it will use metadata.
   *
   * @public
   * @readonly
   * @type {SubtitlesKind}
   * */
  @Prop() public subtitlesKind?: SubtitlesKind;

  /**
   * Language of the track text data. It must be a valid BCP 47 language tag. If the kind attribute is set to subtitles, then srclang must be defined.
   *
   * @public
   * @readonly
   * @type {string}
   * */
  @Prop() public subtitlesLanguage?: string;

  @State() private computedOpenModal = false;

  @State() private origin: Origin;

  @State() private isPlaying: boolean;

  private computedImageSrc: string;

  private computedImageSrcSet: string;

  private computedVideoSrc: string;

  private dialogElement: QdsDialog;

  private externalId: string;

  private parameters: string;

  private videoElement: HTMLVideoElement;

  private readonly prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  get #computedAutoplay(): boolean {
    return (this.autoPlay ?? false) && !this.prefersReducedMotion;
  }

  get #computedLoop(): boolean | undefined {
    return this.loop ?? undefined;
  }

  // get #computedButtonAction(): ButtonAction {
  //   return this.computedOpenModal ? "modal-button" : "player-button";
  // }

  get #computedMuted(): boolean {
    // return this.#computedAutoplay || (this.muted ?? false);
    return this.muted ?? false;
  }

  get #computedPlayButtonLabel(): string {
    return this.isPlaying
      ? `Pause video ${this.labelName}`
      : `Play video ${this.labelName}`;
  }

  get #imageElement(): JsxElement {
    return (
      <img
        loading={this.loading}
        srcSet={
          this.computedImageSrcSet === "" ? undefined : this.computedImageSrcSet
        }
        src={this.computedImageSrc}
        sizes={this.imageSizes ?? undefined}
        alt={this.imageAlt ?? ""}
        onError={(event: Event) => {
          this.handleOnImageError(event);
        }}
      />
    );
  }

  get #videoElement(): JsxElement {
    return (
      <Fragment>
        <video
          class={{ "open-modal": this.computedOpenModal }}
          aria-label={this.labelName}
          muted={this.#computedMuted}
          autoPlay={this.#computedAutoplay}
          loop={this.#computedLoop}
          ref={(element) => {
            this.videoElement = element as HTMLVideoElement;
          }}
          poster={this.computedImageSrc}
        >
          <track
            default
            kind="captions"
            srcLang={this.subtitlesLanguage}
            src={this.srcSubtitles}
          />
          <source src={this.srcVideo} type={this.type} />
          Your browser does not support the video.
        </video>
        <qds-button
          class="player-button"
          aria-label={this.#computedPlayButtonLabel}
          icon-name={`${
            this.isPlaying ? "rectangle-twin" : "arrow-triangle-right"
          }`}
          importance="emphasized"
          size={this.buttonSize}
          onClick={async () => {
            await this.handlePlayer();
          }}
        />
      </Fragment>
    );
  }

  @Listen("keyup")
  public handleKeyUp(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.videoElement.pause();
      this.isPlaying = false;
    }
  }

  @Watch("imageSrc")
  private imageSrcChanged(newImageSource: string): void {
    const { computedSrc, computedSrcSet } =
      getResponsiveImageSource(newImageSource);

    this.computedImageSrc = computedSrc;
    this.computedImageSrcSet = computedSrcSet;
  }

  public componentWillRender(): Promise<void> | void {
    if (this.imageSrc !== undefined) this.imageSrcChanged(this.imageSrc);

    this.computedVideoSrc = this.getSrc();
    this.computedOpenModal = this.openModal ?? false;

    if (this.origin !== "native") {
      this.computedOpenModal = true;
    }

    if (this.computedOpenModal && this.origin !== "native") {
      this.isPlaying = false;
    }
  }

  public componentWillLoad(): void {
    if (this.imageSrc !== undefined) {
      this.imageSrcChanged(this.imageSrc);
    }
  }

  public connectedCallback(): void {
    this.isPlaying = this.openModal === true ? false : this.#computedAutoplay;
  }

  public render() {
    return (
      <Host>
        {this.srcVideo
          ? this.origin === "native" && this.openModal === false
            ? this.#videoElement
            : this.computedOpenModal && (
                <div class="modal-container">
                  {this.#imageElement}
                  <qds-dialog
                    aria-label={this.labelName}
                    ref={(element: QdsDialog) => {
                      this.dialogElement = element;
                    }}
                  >
                    <qds-button
                      aria-label="Close video modal"
                      class="close-button"
                      id="close-dialog"
                      icon-library="core"
                      icon-name="close"
                      importance="emphasized"
                      onClick={async () => {
                        this.videoElement.pause();
                        this.closeDialog();
                      }}
                      autofocus
                    />
                    {this.origin === "native" ? (
                      <div class="video-container-modal">
                        {this.#videoElement}
                      </div>
                    ) : (
                      // eslint-disable-next-line react/iframe-missing-sandbox
                      <iframe
                        sandbox="allow-same-origin allow-presentation allow-scripts allow-popups"
                        src={this.computedVideoSrc}
                        title={`${this.origin} video player`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture;"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowfullScreen
                      />
                    )}
                  </qds-dialog>
                  <qds-button
                    aria-label="Open video modal"
                    class="modal-button"
                    id="open-dialog"
                    icon-name="arrow-triangle-right"
                    importance="emphasized"
                    size={this.buttonSize}
                    onClick={async () => {
                      this.showDialog();
                      await this.handlePlayer();
                    }}
                  />
                </div>
              )
          : this.#imageElement}
      </Host>
    );
  }

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/class-methods-use-this
  private handleOnImageError(event: Event): void {
    (event.target as HTMLImageElement).setAttribute(
      "src",
      "https://s3-alpha-sig.figma.com/img/3969/a808/f550767d1cc8b907adb9cb70d08827a2?Expires=1739145600&Key-Pair-Id=APKAQ4GOSFWCVNEHN3O4&Signature=KKb~VGj5HwLnk7UfnM9h7iUqKdiwbMbwE7m0NcZIgTXOQ6o0ZF5K5JOiCb~Qw9j6pl6ypQSR197yrzIhtuSZDYsqB9D3SDSbYAtoDdB17uQBprrZczmW4fUObL1aEeQb01JwCHd8zTEBveyHixNdDFEJf0osSjou47s8A6adQCrCwbbjde723Ri-YNU-EmtM11bB8F2Nk0f9vGitROSd0XpRqW7g8dzS-7C5wASvyqvGpSA6CSHGeVagyBif-robqdZd6DfUz1fFjJuHeBPS61ufTQIXNd6py0Urf0sniA3rofr0HEycd8m2ndRtK5JClvqohg5joO3JSQS~t0kW3A__",
    );
  }

  private getSrc(): string {
    if (
      this.srcVideo.includes("youtube") ||
      this.srcVideo.includes("youtu.be")
    ) {
      this.origin = "youtube";
      this.setSrcYoutubeParameters();
      return `https://youtube.com/embed/${this.externalId}${this.parameters}`;
    }

    if (this.srcVideo.includes("youku")) {
      this.origin = "youku";
      this.setSrcYoukuParameters();
      return `https://player.youku.com/embed/${this.externalId}${this.parameters}`;
    }

    this.origin = "native";
    return this.srcVideo;
  }

  private closeDialog(): void {
    this.autoPlay = false;
    this.isPlaying = false;
    this.dialogElement.close();
  }

  private showDialog(): void {
    this.dialogElement.show();
    this.autoPlay = !this.prefersReducedMotion;
  }

  private async handlePlayer(): Promise<void> {
    if (this.isPlaying) {
      this.videoElement.pause();
    }

    if (!this.isPlaying) {
      await this.videoElement.play();
    }

    this.isPlaying = !this.isPlaying;
  }

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/class-methods-use-this
  private getNavigationType(): string {
    const navigation = window.performance.getEntriesByType(
      "navigation",
    )[0] as PerformanceNavigationTiming;
    return navigation.type;
  }

  private setSrcYoutubeParameters(): void {
    const parameterValue = this.#computedAutoplay ? 1 : 0;

    const autoplay = `autoplay=${parameterValue}`;
    const loop = `loop=${parameterValue}`;

    this.parameters = `?rel=0&cc_load_policy=3&mute=1&${loop}&${autoplay}`;
    this.externalId = getYoutubeVideoId(this.srcVideo);
  }

  private setSrcYoukuParameters(): void {
    const parameterValue = this.#computedAutoplay ? 1 : 0;

    const autoplay = `autoplay=${parameterValue}`;
    const loop = `loop=${parameterValue}`;

    this.parameters = `?rel=0&mute=1&${loop}&${autoplay}`;
    this.externalId = getYoukuVideoId(this.srcVideo);
  }
}
